import { useCallback, useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import toast from 'react-hot-toast';
import { useNavigate, useParams } from 'react-router-dom';
import {
  loadStripe,
  Stripe,
  StripeCardElement,
  StripeCardElementChangeEvent,
  TokenResult,
} from '@stripe/stripe-js';

import {
  namespaces,
  useSharedTranslation,
} from 'us-react-components/dist/hooks/useSharedTranslation';
import { axios, CreditCard as CreditCardType, CreditCardService } from 'us-web-services';
import { getLongDateString } from 'us-react-components/dist/utils/date';

import { Button, ButtonType } from '../../components';
import { useDependencies } from '../../dependencies';
import { useRecaptcha } from '../../hooks/useRecaptcha';
import { ConfigService } from '../../services/ConfigService/ConfigService';

const config = new ConfigService();

const getCardLabel = (type?: string): string => {
  switch ((type || '').toLowerCase()) {
    case 'american-express': return 'American Express Card';
    case 'discover': return 'Discover Card';
    case 'master-card': return 'Mastercard';
    case 'visa': return 'Visa Card';
    default: return 'Card Number';
  }
};

export const CreditCard = observer((): JSX.Element => {
  const { t } = useSharedTranslation(namespaces.caregiver);
  const navigate = useNavigate();
  const { id: pathId } = useParams<{ id: string }>();
  const { stores } = useDependencies();
  const user = stores.user.user;

  const { executeRecaptcha } = useRecaptcha(config.RECAPTCHA_KEY || '');

  const [card, setCard] = useState<CreditCardType | null>(null);
  const [loading, setLoading] = useState(true);
  const [stripe, setStripe] = useState<Stripe | null>(null);
  const [cardElement, setCardElement] = useState<StripeCardElement | null>(null);
  const [cardFilled, setCardFilled] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [cardNode, setCardNode] = useState<HTMLDivElement | null>(null);
  const cardNodeRef = useCallback((node: HTMLDivElement | null): void => {
    setCardNode(node);
  }, []);

  useEffect(() => {
    document.title = 'UrbanSitter - Payment Method';
  }, []);

  useEffect(() => {
    if (!user?.id) return;
    if (pathId && Number(pathId) !== user.id) {
      navigate(`/user/${user.id}/credit-card`, { replace: true });
      return;
    }
    const load = async (): Promise<void> => {
      try {
        const resp = await CreditCardService.findByFilter({ params: { active: true, userId: user.id } });
        const existing = (resp.data?.data ?? [])[0] ?? null;
        setCard(existing);
      } catch (err) {
        console.warn('[CreditCard] load failed', err);
      } finally {
        setLoading(false);
      }
    };
    void load();
  }, [user?.id, pathId, navigate]);

  useEffect(() => {
    if (card?.id) return;
    if (!config.STRIPE_KEY) return;
    let mounted = true;
    void (async (): Promise<void> => {
      const s = await loadStripe(config.STRIPE_KEY);
      if (!mounted || !s) return;
      const el = s.elements().create('card', {
        style: {
          base: {
            color: '#111',
            fontFamily: '"Helvetica Neue", Helvetica, sans-serif',
            fontSmoothing: 'antialiased',
            fontSize: '16px',
            '::placeholder': { color: '#aab7c4' },
          },
        },
      });
      el.on('change', (e: StripeCardElementChangeEvent): void => {
        setCardFilled(e.complete);
      });
      setStripe(s);
      setCardElement(el);
    })();
    return (): void => { mounted = false; };
  }, [card?.id]);

  useEffect(() => {
    if (!cardElement || !cardNode) return undefined;
    cardElement.mount(cardNode);
    return (): void => {
      try {
        cardElement.unmount();
      } catch {
        /* element may already be unmounted */
      }
    };
  }, [cardElement, cardNode]);

  const addCard = async (): Promise<void> => {
    if (!stripe || !cardElement || !user?.id) return;
    if (!termsAccepted) {
      toast.error(t('pages.creditCard.mustAgreeToTerms'));
      return;
    }
    setSubmitting(true);
    try {
      const result: TokenResult = await stripe.createToken(cardElement);
      if (!result.token) {
        toast.error(result.error?.message ?? t('pages.creditCard.tokenizeError'));
        setSubmitting(false);
        return;
      }
      const recaptchaToken = await executeRecaptcha('creditcard');
      const resp = await CreditCardService.create({
        paymentToken: result.token.id,
        userId: user.id,
        recaptchaToken,
      });
      const saved = (resp.data as { data?: CreditCardType })?.data ?? null;
      setCard(saved);
      toast.success(t('pages.creditCard.savedSuccess'));
    } catch (err) {
      console.warn('[CreditCard] save failed', err);
      if (axios.isAxiosError(err) && err.response?.data?.errors?.[0]?.message) {
        toast.error(err.response.data.errors[0].message);
      } else {
        toast.error(t('pages.creditCard.saveError'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const removeCard = async (): Promise<void> => {
    if (!card?.id) return;
    setSubmitting(true);
    setConfirmOpen(false);
    try {
      await CreditCardService.delete(card.id, { params: { userId: user?.id } });
      setCard(null);
      toast.success(t('pages.creditCard.removedSuccess'));
    } catch (err) {
      console.warn('[CreditCard] remove failed', err);
      toast.error(t('pages.creditCard.removeError'));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div className='container py-6' />;
  }

  return (
    <div className='container py-6 max-w-xl mx-auto'>
      <h1 className='text-2xl font-semibold mb-2'>{t('pages.creditCard.heading')}</h1>
      <p className='text-sm text-neutral-700 mb-4'>
        {user?.parent ? t('pages.creditCard.parentMessage') : t('pages.creditCard.sitterMessage')}
      </p>

      <div className='us-card rounded-[20px]'>
        <div className='text-right text-sm text-neutral-700'>
          {t('pages.creditCard.securedByStripe')}
          <img
            src='https://assets.urbansitter.com/static/img/icons/lock.png'
            alt='Security lock'
            className='inline-block ml-2 align-middle'
          />
        </div>
        <hr className='my-3' />

        {card?.id ? (
          <div>
            <div className='mb-4'>
              <p>
                <span className='font-semibold'>{getCardLabel(card.type)}: </span>
                {t('pages.creditCard.endingIn', { last4: card.number?.slice(-4) ?? '----' })}
              </p>
              <p>
                <span className='font-semibold'>{t('pages.creditCard.expirationDate')} </span>
                {card.expirationMonth}/{card.expirationYear}
              </p>
              {card.zip && (
                <p>
                  <span className='font-semibold'>{t('pages.creditCard.billingZipCode')} </span>
                  {card.zip}
                </p>
              )}
              {card.created && (
                <p className='font-semibold mt-3'>
                  {t('pages.creditCard.verifiedOn', {
                    date: getLongDateString(card.created),
                  })}
                </p>
              )}
            </div>
            <Button
              id='delete-credit-card'
              buttonType={ButtonType.secondary}
              className='mr-3'
              disabled={submitting}
              onClick={(): void => setConfirmOpen(true)}
            >
              {t('pages.creditCard.removeCta')}
            </Button>
          </div>
        ) : (
          <div id='credit-card-entry'>
            <div className='mb-5'>
              <div id='card-element' ref={cardNodeRef} className='p-3 border border-neutral-300 rounded' />
            </div>
            <label htmlFor='credit-card-terms' className='flex items-start gap-2 mb-3 text-sm cursor-pointer'>
              <input
                id='credit-card-terms'
                type='checkbox'
                checked={termsAccepted}
                onChange={(): void => setTermsAccepted((v) => !v)}
                className='mt-1'
              />
              <span>
                {t('pages.creditCard.termsPrefix')}{' '}
                <a
                  id='payment-terms'
                  href='/payment-terms'
                  target='_blank'
                  rel='noreferrer'
                  className='underline'
                >
                  {t('pages.creditCard.termsLink')}
                </a>
                {t('pages.creditCard.termsSuffix')}
              </span>
            </label>
            <Button
              id='add-credit-card'
              buttonType={ButtonType.primary}
              className='w-full mt-5'
              disabled={submitting || !termsAccepted || !cardFilled}
              onClick={addCard}
            >
              {t('pages.creditCard.addCta')}
            </Button>
          </div>
        )}
      </div>

      {card?.id && (
        <p className='text-sm mt-2'>{t('pages.creditCard.existingMessage')}</p>
      )}

      {!user?.parent && (
        <div className='mt-8'>
          <h1 className='text-2xl font-semibold mb-2'>
            {t('pages.creditCard.payoutsHeading')}
          </h1>
          <p className='mb-4'>{t('pages.creditCard.payoutsBody')}</p>
          <Button
            id='manage-payouts'
            buttonType={ButtonType.secondary}
            onClick={(): void => { window.location.href = '/app/sitter/payout'; }}
          >
            {t('pages.creditCard.managePayouts')}
          </Button>
        </div>
      )}

      {confirmOpen && (
        <div className='fixed inset-0 bg-black/50 flex items-center justify-center z-50'>
          <div className='bg-white rounded p-6 max-w-sm mx-4'>
            <h3 className='font-semibold text-lg'>{t('pages.creditCard.confirmRemoveHeading')}</h3>
            <p className='mt-2'>{t('pages.creditCard.confirmRemoveBody')}</p>
            <div className='flex justify-end gap-2 mt-4'>
              <Button buttonType={ButtonType.secondary} onClick={(): void => setConfirmOpen(false)}>
                {t('common.cancel')}
              </Button>
              <Button buttonType={ButtonType.primary} onClick={removeCard} disabled={submitting}>
                {t('pages.creditCard.confirmRemove')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
});
